/**
 * HR Service - API calls for HR module entities
 * Handles: Recruitment, Training, Evaluations, Leaves, Employee Contracts
 */

import { demoHttpApiCall } from '../../online-demo/services/demoHttpApiBridge';
import { isCloudDemoRuntime } from './publicDemoRuntime';
import i18n from '../../i18n';

const cm = (key, opts) => i18n.t(`serviceMessages.common.${key}`, opts);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const IS_CLOUD_DEMO_BROWSER =
  isCloudDemoRuntime() &&
  typeof window !== 'undefined' &&
  !(typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.get === 'function');

const normalizeDemoApiResult = (result) => {
  if (Array.isArray(result)) return { data: result };
  if (
    result &&
    typeof result === 'object' &&
    result.data === undefined &&
    result.success === undefined &&
    result.error === undefined &&
    result.id != null
  ) {
    return { data: result };
  }
  return result;
};

// Helper function to make API calls
const apiCall = async (endpoint, method = 'GET', data = null) => {
  if (IS_CLOUD_DEMO_BROWSER) {
    const raw = await demoHttpApiCall(endpoint, method, data);
    return normalizeDemoApiResult(raw);
  }

  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (data) {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, options);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || cm('http_error', { status: response.status }));
    }
    
    return result;
  } catch (error) {
    console.error(`API Error (${method} ${endpoint}):`, error);
    throw error;
  }
};

// =====================================================
// RECRUITMENT CANDIDATES
// =====================================================

export const getCandidates = async () => {
  const result = await apiCall('/recruitment_candidates');
  return result.data || [];
};

export const getCandidate = async (id) => {
  const result = await apiCall(`/recruitment_candidates/${id}`);
  return result.data;
};

export const createCandidate = async (candidateData) => {
  const now = new Date().toISOString();
  const data = {
    ...candidateData,
    applied_date: candidateData.appliedDate || now,
    cv_url: candidateData.cvUrl || '',
    created_at: now,
    updated_at: now,
  };
  const result = await apiCall('/recruitment_candidates', 'POST', data);
  return result.data;
};

export const updateCandidate = async (id, candidateData) => {
  const data = {
    ...candidateData,
    applied_date: candidateData.appliedDate,
    cv_url: candidateData.cvUrl || '',
    updated_at: new Date().toISOString(),
  };
  const result = await apiCall(`/recruitment_candidates/${id}`, 'PUT', data);
  return result.data;
};

export const deleteCandidate = async (id) => {
  const result = await apiCall(`/recruitment_candidates/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// TRAINING SESSIONS
// =====================================================

export const getTrainingSessions = async () => {
  const result = await apiCall('/training_sessions');
  return result.data || [];
};

export const getTrainingSession = async (id) => {
  const result = await apiCall(`/training_sessions/${id}`);
  return result.data;
};

export const createTrainingSession = async (trainingData) => {
  const now = new Date().toISOString();
  const data = {
    ...trainingData,
    start_date: trainingData.startDate,
    end_date: trainingData.endDate,
    participants: JSON.stringify(trainingData.participants || []),
    created_at: now,
    updated_at: now,
  };
  const result = await apiCall('/training_sessions', 'POST', data);
  return result.data;
};

export const updateTrainingSession = async (id, trainingData) => {
  const data = {
    ...trainingData,
    start_date: trainingData.startDate,
    end_date: trainingData.endDate,
    participants: JSON.stringify(trainingData.participants || []),
    updated_at: new Date().toISOString(),
  };
  const result = await apiCall(`/training_sessions/${id}`, 'PUT', data);
  return result.data;
};

export const deleteTrainingSession = async (id) => {
  const result = await apiCall(`/training_sessions/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// EMPLOYEE EVALUATIONS
// =====================================================

export const getEvaluations = async () => {
  const result = await apiCall('/hr/evaluations');
  return result.data || [];
};

export const getEvaluation = async (id) => {
  const result = await apiCall(`/hr/evaluations/${id}`);
  return result.data;
};

export const createEvaluation = async (evaluationData) => {
  const now = new Date().toISOString();
  const data = {
    ...evaluationData,
    employee_name: evaluationData.employeeName,
    employee_id: evaluationData.employeeId || null,
    evaluation_date: evaluationData.date,
    note_globale: evaluationData.noteGlobale,
    created_at: now,
    updated_at: now,
  };
  const result = await apiCall('/hr/evaluations', 'POST', data);
  return result.data;
};

export const updateEvaluation = async (id, evaluationData) => {
  const data = {
    ...evaluationData,
    employee_name: evaluationData.employeeName,
    employee_id: evaluationData.employeeId || null,
    evaluation_date: evaluationData.date,
    note_globale: evaluationData.noteGlobale,
    updated_at: new Date().toISOString(),
  };
  const result = await apiCall(`/hr/evaluations/${id}`, 'PUT', data);
  return result.data;
};

export const deleteEvaluation = async (id) => {
  const result = await apiCall(`/hr/evaluations/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// EMPLOYEE LEAVES
// =====================================================

export const getLeaves = async () => {
  const result = await apiCall('/hr/leaves');
  return result.data || [];
};

export const getLeave = async (id) => {
  const result = await apiCall(`/hr/leaves/${id}`);
  return result.data;
};

export const createLeave = async (leaveData) => {
  const now = new Date().toISOString();
  const data = {
    ...leaveData,
    employee_name: leaveData.employeeName,
    employee_id: leaveData.employeeId || null,
    leave_type: leaveData.type,
    start_date: leaveData.startDate,
    end_date: leaveData.endDate,
    approved_by: leaveData.approvedBy || null,
    approved_date: leaveData.approvedDate || null,
    created_at: now,
    updated_at: now,
  };
  const result = await apiCall('/hr/leaves', 'POST', data);
  return result.data;
};

export const updateLeave = async (id, leaveData) => {
  const data = {
    ...leaveData,
    employee_name: leaveData.employeeName,
    employee_id: leaveData.employeeId || null,
    leave_type: leaveData.type,
    start_date: leaveData.startDate,
    end_date: leaveData.endDate,
    approved_by: leaveData.approvedBy || null,
    approved_date: leaveData.approvedDate || null,
    updated_at: new Date().toISOString(),
  };
  const result = await apiCall(`/hr/leaves/${id}`, 'PUT', data);
  return result.data;
};

export const deleteLeave = async (id) => {
  const result = await apiCall(`/hr/leaves/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// EMPLOYEE CONTRACTS
// =====================================================

export const getEmployeeContracts = async () => {
  try {
    const result = await apiCall('/hr/contracts');
    return result.data || [];
  } catch (error) {
    console.warn('Employee contracts endpoint not available yet, using empty array');
    return [];
  }
};

export const getEmployeeContract = async (id) => {
  const result = await apiCall(`/hr/contracts/${id}`);
  return result.data;
};

export const createEmployeeContract = async (contractData) => {
  const now = new Date().toISOString();
  const data = {
    ...contractData,
    employee_name: contractData.employeeName,
    employee_id: contractData.employeeId || null,
    contract_type: contractData.contractType,
    start_date: contractData.startDate,
    end_date: contractData.endDate || null,
    probation_period: contractData.probationPeriod,
    work_hours: contractData.workHours,
    created_at: now,
    updated_at: now,
  };
  const result = await apiCall('/hr/contracts', 'POST', data);
  return result.data;
};

export const updateEmployeeContract = async (id, contractData) => {
  const data = {
    ...contractData,
    employee_name: contractData.employeeName,
    employee_id: contractData.employeeId || null,
    contract_type: contractData.contractType,
    start_date: contractData.startDate,
    end_date: contractData.endDate || null,
    probation_period: contractData.probationPeriod,
    work_hours: contractData.workHours,
    updated_at: new Date().toISOString(),
  };
  const result = await apiCall(`/hr/contracts/${id}`, 'PUT', data);
  return result.data;
};

export const deleteEmployeeContract = async (id) => {
  const result = await apiCall(`/hr/contracts/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// HR DOCUMENTS
// =====================================================

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

// Backend HR routes return camelCase via convert*ToFrontend; support both shapes.
// Transform API data to frontend format for candidates
export const transformCandidate = (apiData) => {
  if (!apiData) return null;
  return {
    id: apiData.id,
    name: apiData.name ?? '',
    email: apiData.email ?? '',
    phone: apiData.phone ?? '',
    position: apiData.position ?? '',
    experience: apiData.experience ?? '',
    education: apiData.education ?? '',
    status: apiData.status ?? '',
    appliedDate: apiData.applied_date ?? apiData.appliedDate ?? '',
    cvUrl: apiData.cv_url ?? apiData.cvUrl ?? '',
    notes: apiData.notes ?? '',
  };
};

// Transform API data to frontend format for training sessions
export const transformTrainingSession = (apiData) => {
  if (!apiData) return null;
  let participants = apiData.participants;
  if (typeof participants === 'string') {
    try {
      participants = JSON.parse(participants);
    } catch {
      participants = [];
    }
  }
  if (!Array.isArray(participants)) participants = [];
  return {
    id: apiData.id,
    title: apiData.title ?? '',
    description: apiData.description ?? '',
    trainer: apiData.trainer ?? '',
    startDate: apiData.start_date ?? apiData.startDate ?? '',
    endDate: apiData.end_date ?? apiData.endDate ?? '',
    duration: apiData.duration,
    participants,
    status: apiData.status ?? '',
    category: apiData.category ?? '',
    cost: apiData.cost,
    location: apiData.location ?? '',
    notes: apiData.notes ?? '',
  };
};

// Transform API data to frontend format for evaluations.
// Backend /hr/evaluations returns camelCase (convertEvaluationToFrontend); raw DB rows use snake_case — support both.
export const transformEvaluation = (apiData) => {
  if (!apiData) {
    return null;
  }
  const note =
    apiData.note_globale ?? apiData.noteGlobale ?? null;
  return {
    id: apiData.id,
    employeeId: apiData.employee_id ?? apiData.employeeId ?? null,
    employeeName: apiData.employee_name ?? apiData.employeeName ?? '',
    evaluator: apiData.evaluator ?? '',
    period: apiData.period ?? '',
    date:
      apiData.evaluation_date ??
      apiData.date ??
      apiData.evaluationDate ??
      '',
    performance: Number(apiData.performance) || 3,
    competences: Number(apiData.competences) || 3,
    comportement: Number(apiData.comportement) || 3,
    objectifs: Number(apiData.objectifs) || 3,
    noteGlobale: note != null && !Number.isNaN(Number(note)) ? Number(note) : 3,
    strengths: apiData.strengths ?? '',
    weaknesses: apiData.weaknesses ?? '',
    objectives: apiData.objectives ?? '',
    comments: apiData.comments ?? '',
  };
};

// Transform API data to frontend format for leaves
export const transformLeave = (apiData) => {
  if (!apiData) return null;
  const d = apiData.days;
  return {
    id: apiData.id,
    employeeId: apiData.employee_id ?? apiData.employeeId ?? null,
    employeeName: apiData.employee_name ?? apiData.employeeName ?? '',
    type: apiData.leave_type ?? apiData.type ?? '',
    startDate: apiData.start_date ?? apiData.startDate ?? '',
    endDate: apiData.end_date ?? apiData.endDate ?? '',
    days: d != null && d !== '' && !Number.isNaN(Number(d)) ? Number(d) : 0,
    status: apiData.status ?? 'pending',
    reason: apiData.reason ?? '',
    approvedBy: apiData.approved_by ?? apiData.approvedBy ?? '',
    approvedDate: apiData.approved_date ?? apiData.approvedDate ?? '',
    comments: apiData.comments ?? '',
  };
};

// Transform API data to frontend format for employee contracts
export const transformEmployeeContract = (apiData) => {
  if (!apiData) return null;
  return {
    id: apiData.id,
    employeeId: apiData.employee_id ?? apiData.employeeId ?? null,
    employeeName: apiData.employee_name ?? apiData.employeeName ?? '',
    contractType: apiData.contract_type ?? apiData.contractType ?? '',
    position: apiData.position ?? '',
    department: apiData.department ?? '',
    startDate: apiData.start_date ?? apiData.startDate ?? '',
    endDate: apiData.end_date ?? apiData.endDate ?? '',
    salary: apiData.salary,
    status: apiData.status ?? '',
    probationPeriod: apiData.probation_period ?? apiData.probationPeriod,
    workHours: apiData.work_hours ?? apiData.workHours,
    notes: apiData.notes ?? '',
  };
};

export default {
  // Candidates
  getCandidates,
  getCandidate,
  createCandidate,
  updateCandidate,
  deleteCandidate,
  transformCandidate,
  
  // Training
  getTrainingSessions,
  getTrainingSession,
  createTrainingSession,
  updateTrainingSession,
  deleteTrainingSession,
  transformTrainingSession,
  
  // Evaluations
  getEvaluations,
  getEvaluation,
  createEvaluation,
  updateEvaluation,
  deleteEvaluation,
  transformEvaluation,
  
  // Leaves
  getLeaves,
  getLeave,
  createLeave,
  updateLeave,
  deleteLeave,
  transformLeave,
  
  // Employee Contracts
  getEmployeeContracts,
  getEmployeeContract,
  createEmployeeContract,
  updateEmployeeContract,
  deleteEmployeeContract,
  transformEmployeeContract,
};


